import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

@Directive({
  selector: '[sgtTooltip]',
})
export class TooltipDirective implements OnInit, OnDestroy, OnChanges {
  @Input('sgtTooltip') tooltipText = '';
  @Input() tooltipPosition: TooltipPosition = 'bottom';
  @Input() sgtTooltipClass: string | string[] = '';

  private tooltipElement: HTMLElement | null = null;
  private appliedCustomClasses: string[] = [];
  private POSITIONS: TooltipPosition[] = ['top', 'bottom', 'left', 'right'];
  private unlistenMouseEnter!: () => void;
  private unlistenMouseLeave!: () => void;
  private unlistenMouseDown!: () => void;
  private unlistenScroll: (() => void) | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
  ) { }

  ngOnInit(): void {
    this.unlistenMouseEnter = this.renderer.listen(
      this.el.nativeElement,
      'mouseenter',
      () => this.show(),
    );

    this.unlistenMouseLeave = this.renderer.listen(
      this.el.nativeElement,
      'mouseleave',
      () => this.hide(),
    );

    this.unlistenMouseDown = this.renderer.listen(
      this.el.nativeElement,
      'mousedown',
      () => this.hide(),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tooltipText'] && this.tooltipElement) {
      this.hide();
      return;
    }

    if (changes['sgtTooltipClass'] && this.tooltipElement) {
      this.applyCustomClasses();
      this.setTooltipPosition();
    }
  }

  private show(): void {
    if (!this.tooltipText || this.tooltipElement || this.hasOverlayOpen()) {
      return;
    }

    this.removeOrphanTooltips();

    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipElement, 'sgt-tooltip');
    this.renderer.addClass(
      this.tooltipElement,
      `sgt-tooltip--${this.tooltipPosition}`,
    );
    this.applyCustomClasses();

    const text = this.renderer.createText(this.tooltipText);
    this.renderer.appendChild(this.tooltipElement, text);
    this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'none');
    this.renderer.appendChild(document.body, this.tooltipElement);

    this.setTooltipPosition();
    this.setupScrollListener();
  }

  private applyCustomClasses(): void {
    if (!this.tooltipElement) {
      return;
    }
    const el = this.tooltipElement;

    this.appliedCustomClasses.forEach(cls => {
      this.renderer.removeClass(el, cls);
    });

    const next = this.normalizeClassInput(this.sgtTooltipClass);
    next.forEach(cls => {
      this.renderer.addClass(el, cls);
    });

    this.appliedCustomClasses = next;
  }

  private normalizeClassInput(input: string | string[]): string[] {
    if (!input) {
      return [];
    }
    const tokens = Array.isArray(input) ? input : input.split(' ');
    return tokens.map(t => t.trim()).filter(t => t.length > 0);
  }

  private hasOverlayOpen(): boolean {
    return !!document.querySelector('.cdk-overlay-container .cdk-overlay-pane');
  }

  private setupScrollListener(): void {
    const handler = () => this.hide();
    document.addEventListener('scroll', handler, true);
    this.unlistenScroll = () => document.removeEventListener('scroll', handler, true);
  }

  private hide(): void {
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
      this.appliedCustomClasses = [];
    }
    this.unlistenScroll?.();
    this.unlistenScroll = null;
  }

  private removeOrphanTooltips(): void {
    document
      .querySelectorAll('.sgt-tooltip')
      .forEach(el => this.renderer.removeChild(document.body, el));
  }

  private setTooltipPosition(): void {
    if (!this.tooltipElement) {
      return;
    }

    const hostRect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const gap = 8;
    const margin = 4;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const all: Array<TooltipPosition> = this.POSITIONS;
    const order = [this.tooltipPosition, ...all.filter(p => p !== this.tooltipPosition)];

    let chosenPosition = this.tooltipPosition;
    let chosenCoords = this.calcCoords(this.tooltipPosition, hostRect, tooltipRect, gap);

    for (const pos of order) {
      const coords = this.calcCoords(pos, hostRect, tooltipRect, gap);
      if (
        coords.left >= margin &&
        coords.top >= margin &&
        coords.left + tooltipRect.width <= vw - margin &&
        coords.top + tooltipRect.height <= vh - margin
      ) {
        chosenPosition = pos;
        chosenCoords = coords;
        break;
      }
    }

    const top = Math.max(margin, Math.min(chosenCoords.top, vh - tooltipRect.height - margin));
    const left = Math.max(margin, Math.min(chosenCoords.left, vw - tooltipRect.width - margin));

    this.updatePositionClass(chosenPosition);

    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
  }

  private calcCoords(
    position: TooltipPosition,
    hostRect: DOMRect,
    tooltipRect: DOMRect,
    gap: number,
  ): { top: number; left: number } {
    switch (position) {
      case 'bottom':
        return {
          top: hostRect.bottom + gap,
          left: hostRect.left + (hostRect.width - tooltipRect.width) / 2,
        };
      case 'left':
        return {
          top: hostRect.top + (hostRect.height - tooltipRect.height) / 2,
          left: hostRect.left - tooltipRect.width - gap,
        };
      case 'right':
        return {
          top: hostRect.top + (hostRect.height - tooltipRect.height) / 2,
          left: hostRect.right + gap,
        };
      case 'top':
      default:
        return {
          top: hostRect.top - tooltipRect.height - gap,
          left: hostRect.left + (hostRect.width - tooltipRect.width) / 2,
        };
    }
  }

  private updatePositionClass(position: TooltipPosition): void {
    if (!this.tooltipElement) {
      return;
    }
    const el = this.tooltipElement;
    this.POSITIONS.forEach(pos => {
      this.renderer.removeClass(el, `sgt-tooltip--${pos}`);
    });
    this.renderer.addClass(el, `sgt-tooltip--${position}`);
  }

  ngOnDestroy(): void {
    this.hide();
    this.unlistenMouseEnter?.();
    this.unlistenMouseLeave?.();
    this.unlistenMouseDown?.();
    this.unlistenScroll?.();
    this.unlistenScroll = null;
  }
}
